import { IconErrorSmall, IconInfoSmall, IconSuccessSmall, IconWarningSmall } from 'assets'
import { DashboardCardDTO, NotificationType } from 'models'
import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { Api } from 'utilities'
import { DashboardCardProps, DashboardDisplayData } from './DashboardCard.types'

export const useMaxWidthCorrection = (): MutableRefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null)

  const updateMaxWidth = () => {
    const parentWidth = containerRef.current?.parentElement?.clientWidth
    const childrenCount = containerRef.current?.parentElement?.childElementCount
    if (!parentWidth || !childrenCount) return

    const containerMaxWidth = parentWidth / childrenCount - 20 // -20px - this is the left padding for each card
    containerRef.current.style.maxWidth = containerMaxWidth + 'px'
  }

  useEffect(() => {
    if (!containerRef.current) return

    updateMaxWidth()
    window.addEventListener('resize', updateMaxWidth, false)

    return () => window.removeEventListener('resize', updateMaxWidth, false)
  }, [containerRef])

  return containerRef
}

export const useDisplayData = (
  apiUrl: DashboardCardProps['apiUrl'],
  apiType: DashboardCardProps['apiType'],
  totalIsMainValue: DashboardCardProps['totalIsMainValue']
): { displayData: DashboardDisplayData, isLoading: boolean } => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [displayData, setDisplayData] = useState<DashboardDisplayData>()

  useEffect(() => {
    Api
      .get<DashboardCardDTO>(apiUrl, apiType)
      .then(data => {
        const {
          count,
          delta,
          notifications,
          notificationSummary,
          total
        } = data

        let displayNotifications

        if (notifications) {
          notifications.forEach(({ notificationType }) => {
            if (!displayNotifications) {
              displayNotifications = {}
            }
            if (!displayNotifications[notificationType]) {
              displayNotifications[notificationType] = 1
            } else {
              displayNotifications[notificationType]++
            }
          })
        }

        setDisplayData({
          value: count,
          valueSecondary: delta
            ? `+ ${delta} new`
            : total ? `${total} total` : '',
          valueSecondaryPrimaryColor: Boolean(delta),
          notifications: apiUrl == "/project/info" ? notificationSummary :  displayNotifications // Set the condition for /project/info to modify the response of notifications
        })
        setIsLoading(false)
      })
      .catch(err => console.error(err))
  }, [apiUrl, apiType, totalIsMainValue])

  return {
    displayData,
    isLoading
  }
}

export const getIconByNotificationType = (
  type: NotificationType
): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => {
  switch (type) {
    case NotificationType.WARNING: return IconWarningSmall
    case NotificationType.ERROR: return IconErrorSmall
    case NotificationType.INFO: return IconInfoSmall
    case NotificationType.OK: return IconSuccessSmall
    default: return null
  }
}
